import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import {
  cancelWorkflow,
  getWorkflow,
  STATUS_COLOR,
  WorkflowDetail,
} from "../../api/workflows";
import { MeResponse } from "../../api/me";
import { costForWorkflow, formatCost } from "../../api/cost";

const TERMINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);

// Build the wizard URL from a workflow's saved params. We deliberately
// drop `output` so the wizard re-suggests a fresh timestamped prefix —
// reusing the previous output would clobber the prior export's files.
// We DO carry ``output_format`` so a cloned condensed-json export
// re-runs as condensed-json (without it, the wizard would silently
// default the clone back to xml and the user would lose access to
// the search-env affordance on the new workflow).
function cloneUrlFor(wf: WorkflowDetail): string | null {
  if (wf.kind !== "batch_export") return null;
  let params: {
    months?: string[];
    filter?: string;
    search?: string;
    output_format?: string;
    description?: string;
    content_types?: string[];
    content_type?: string;
  };
  try {
    params = JSON.parse(wf.params_json);
  } catch {
    return null;
  }
  const qs = new URLSearchParams();
  if (params.months?.length) qs.set("months", params.months.join(","));
  if (params.filter) qs.set("filter", params.filter);
  if (params.search) qs.set("search", params.search);
  if (params.output_format) qs.set("output_format", params.output_format);
  if (params.description) qs.set("description", params.description);
  // Carry the corpus selection so a cloned podcast/mixed export doesn't
  // silently fall back to broadcast. Prefer the multi-select
  // ``content_types``; fall back to the legacy scalar ``content_type``.
  const cts = params.content_types?.length
    ? params.content_types
    : params.content_type
      ? [params.content_type]
      : [];
  if (cts.length) qs.set("content_types", cts.join(","));
  return `/exports/new?${qs.toString()}`;
}

function descriptionFor(wf: WorkflowDetail): string | null {
  try {
    const params = JSON.parse(wf.params_json) as { description?: unknown };
    if (typeof params.description === "string" && params.description.trim()) {
      return params.description.trim();
    }
  } catch {
    // ignore
  }
  return null;
}

// Top-of-page header for the workflow-detail view: id + status chip,
// optional Clone (for batch_export — re-enters the wizard with the
// original params) and Cancel buttons, the error_summary alert when
// the workflow has failed, and the "confirm cancel" dialog.
//
// Owns the cancel-flow state internally (confirmCancel toggle +
// cancelling spinner + the API call). On successful cancel + refetch
// it fires `onCancelled` so the parent can fold the new workflow
// shape into its state; errors go through `onError`.
interface Props {
  wf: WorkflowDetail;
  me: MeResponse | null;
  onCancelled: (fresh: WorkflowDetail) => void;
  onError: (msg: string) => void;
}

export function WorkflowHeader({ wf, me, onCancelled, onError }: Props) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isTerminal = TERMINAL_STATUSES.has(wf.status);
  const cloneTo = cloneUrlFor(wf);
  const description = descriptionFor(wf);
  const rates = me
    ? { cpuHrUsd: me.cost_cpu_hr_usd, memGibHrUsd: me.cost_mem_gib_hr_usd }
    : null;
  const totalCost = rates ? costForWorkflow(wf.jobs, rates) : null;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelWorkflow(wf.id);
      const fresh = await getWorkflow(wf.id);
      onCancelled(fresh);
    } catch (err: unknown) {
      onError(String(err));
    } finally {
      setCancelling(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="h5">
            <code>{wf.id}</code>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {wf.kind} · submitted by {wf.submitted_by}
            {totalCost !== null && totalCost > 0 && (
              <> · est. cost {formatCost(totalCost)}</>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={wf.status} color={STATUS_COLOR[wf.status] ?? "default"} />
          {cloneTo && (
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => navigate(cloneTo)}
            >
              Clone
            </Button>
          )}
          {!isTerminal && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setConfirmOpen(true)}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>

      {description && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {wf.error_summary && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {wf.error_summary}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cancel this workflow?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This stops every running Job under it and marks the workflow
            cancelled. Any partial output already on S3 stays where it is.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={cancelling}>
            Keep running
          </Button>
          <Button
            onClick={handleCancel}
            color="warning"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? "Cancelling…" : "Cancel workflow"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
